/* eslint no-eval: 0 */
import React from "react";
//import axios from "axios";
import moment from "moment";
import classnames from "classnames";
import { Parallax } from "react-scroll-parallax";
import PropTypes from "prop-types";

// Mui Components
import {
  MuiThemeProvider,
  createMuiTheme,
  withStyles
} from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper"; // eslint-disable-line
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent"; // eslint-disable-line
import DialogContentText from "@material-ui/core/DialogContentText"; // eslint-disable-line
import Slide from "@material-ui/core/Slide";
import Avatar from "@material-ui/core/Avatar";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import Grow from "@material-ui/core/Grow";

// App Assets
import mountainsImg from "../public/mountains.png";
//const mountainsImgLqip = lqip("../public/mountains.png");
import treesImg from "../public/trees.png";
import doggoImg from "../public/doggo.png";

const headerMinHeight = 520;
const bodyHeaderImgOverlap = 200;

const color = {
  blue: "#7dbbc8",
  gold: "#daa921",
  greenLight: "#8e9d61",
  greenDark: "#474b3c",
  dark: "#1e231d",
  brownLight: "#7a5729",
  brownDark: "#302310",
  rust: "#945a39"
};

const displayFontFamily = "'Playfair Display', serif";
const bodyFontFamily = "'Open Sans', sans-serif";

const theme = createMuiTheme({
  typography: {
    // Use the system font instead of the default Roboto font.
    fontFamily: bodyFontFamily,
    fontSize: 18,
    display1: {
      fontFamily: displayFontFamily,
      fontWeight: 900,
      fontStyle: "italic"
    },
    display2: {
      fontFamily: displayFontFamily,
      fontWeight: 900,
      fontStyle: "italic"
    },
    display3: {
      fontFamily: displayFontFamily,
      fontWeight: 900,
      fontStyle: "italic"
    },
    display4: {
      fontFamily: displayFontFamily,
      fontWeight: 900,
      fontStyle: "italic",
      "@media (max-width:767px)": {
        fontSize: "6rem"
      }
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.46429em",
      color: "rgba(0, 0, 0, 0.87)"
    }
  }
});

const styles = theme => ({
  contentRoot: {
    display: "block",
    width: "100%"
  },
  absolute: {
    position: "absolute",
    overflow: "hidden",
    zIndex: 0
  },
  fullWidth: {
    width: "100%"
  },
  content: {
    position: "relative",
    zIndex: 1
  },
  header: {
    minHeight: `${headerMinHeight}px`,
    textAlign: "center",
    paddingTop: theme.spacing.unit * 15,
    "@media (max-width:767px)": {
      minHeight: "480px"
    }
  },
  marriedStatus: {
    textShadow: "0 1px 10px rgba(0,0,0,0.5)"
  },
  contentBodyContainer: {
    position: "relative"
  },
  mountains: {
    margin: "0 auto",
    minWidth: "100%",
    "@media (max-width:767px)": {
      width: "240%"
    }
  },
  treesContainer: {
    marginBottom: `${-bodyHeaderImgOverlap}px`,
    width: "100%",
    overflow: "hidden"
  },
  trees: {
    minWidth: "100%",
    "@media (max-width:767px)": {
      width: "180%"
    }
  },
  timerContainer: {
    position: "relative",
    marginTop: `${-120}px`,
    padding: theme.spacing.unit * 4,
    "@media (max-width:767px)": {
      marginTop: `${-90}px`
    }
  },
  timer: {
    padding: theme.spacing.unit * 2
  },
  contentBody: {
    paddingTop: `${bodyHeaderImgOverlap}px`,
    backgroundColor: color.gold
  },
  link: {
    textDecoration: "none",
    color: color.dark
  },
  bckgBrown: {
    backgroundColor: color.brownDark
  },
  bckgDark: {
    backgroundColor: color.dark
  },
  textWhite: {
    color: "#fff"
  },
  textGold: {
    color: color.gold
  },
  textDark: {
    color: color.dark
  },
  section: {
    paddingTop: theme.spacing.unit * 8,
    paddingBottom: theme.spacing.unit * 8,
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2
  },
  buttonGroup: {
    paddingTop: theme.spacing.unit * 2,
    paddingBottom: theme.spacing.unit * 2
  },
  question: {
    color: color.rust,
    fontFamily: displayFontFamily,
    fontWeight: 900,
    fontSize: "2.5rem",
    lineHeight: "1.2em",
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2,
    "@media (max-width:767px)": {
      fontSize: "2rem"
    }
  },
  answer: {
    color: color.brownLight,
    fontFamily: bodyFontFamily,
    fontWeight: 400,
    fontSize: "1.5rem",
    lineHeight: "1.3em",
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2,
    paddingBottom: theme.spacing.unit * 6,
    "@media (max-width:767px)": {
      fontSize: "1.25rem"
    }
  },
  answerNoPad: {
    color: color.brownLight,
    fontFamily: bodyFontFamily,
    fontWeight: 400,
    fontSize: "1.5rem",
    lineHeight: "1.3em",
    paddingBottom: theme.spacing.unit * 2,
    "@media (max-width:767px)": {
      fontSize: "1.25rem"
    }
  },
  bigAvatar: {
    width: "250px",
    height: "250px",
    "@media (max-width:767px)": {
      width: "100%",
      height: "unset"
    }
  },
  parallaxImgContainer: {
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    position: "relative",
    margin: "0 auto"
  },
  parallaxImg: {
    width: "100%",
    height: "auto",
    maxWidth: "100%",
    marginBottom: "-4px"
  },
  scrollText: {
    color: color.dark,
    fontSize: "1.2rem",
    textShadow: "0 1px 6px rgba(0,0,0,0.5)",
    fontWeight: 600
  },
  scrollArrow: {
    fill: color.dark,
    width: "36px",
    height: "36px"
  },
  button: {
    padding: theme.spacing.unit * 2
  }
});

function Transition(props) {
  return <Slide direction="up" {...props} />;
}

const weddingDateTime = new Date("2018-09-23T16:00:00");
const noAnswers = ["No.", "Nope.", "Not yet."];

class MarriageAnnouncement extends React.Component {
  state = {
    dialogOpen: false,
    dialogMsg: "I know, right?!",
    response: "",
    married: "",
    tabIndex: 0,
    grow: true
  };

  static contextTypes = {
    parallaxController: PropTypes.object.isRequired
  };

  handleTabChange = (event, tabIndex) => {
    let page = "pricing";

    switch (tabIndex) {
      case 1:
        page = "info";
        break;
      case 2:
        page = "related";
        break;
      case 3:
        page = "notes";
        break;
      case 0:
      default:
        page = "pricing";
        break;
    }
    this.setState({ tabIndex });
    this.props.history.push(this.props.match.url + "/" + page);
  };

  handleChange = name => e => {
    this.setState({
      [name]: e.target.value
    });
  };

  handleClickOpen = msg => {
    this.setState({ dialogOpen: true, dialogMsg: msg });
  };

  handleClose = () => {
    this.setState({ dialogOpen: false });
  };

  handleQuesions = () => {
    this.handleClickOpen("We figured, see below.");
  };

  handleTime = () => {
    this.handleClickOpen("I know, right?!");
  };

  handleUpdates = () => {
    this.handleClickOpen("We'll figure something out. Check back soon.");
  };

  handleImgLoad = () => {
    this.context.parallaxController.update();
  };

  updateScroll = () => {
    let timer = 400;
    if (this.state.grow) {
      timer = 1800;
    }
    setTimeout(() => {
      this.setState({ grow: !this.state.grow }, () => {
        this.updateScroll();
      });
    }, timer);
  };

  isMarried = () => {
    if (Date.now() <= Date.parse(weddingDateTime)) {
      return false;
    } else {
      return true;
    }
  };

  componentDidMount() {
    let msg = "Yes!";
    if (!this.isMarried()) {
      msg = noAnswers[Math.floor(Math.random() * noAnswers.length)];
    }
    this.setState({ married: msg });
    this.updateScroll();
  }

  render() {
    const { classes } = this.props;

    return (
      <MuiThemeProvider theme={theme}>
        <div className={classes.contentRoot}>
          <div className={classnames(classes.absolute, classes.fullWidth)}>
            <Parallax offsetYMax={10} offsetYMin={-2} tag="test">
              <img
                src={mountainsImg}
                className={classes.mountains}
                onLoad={this.handleImgLoad}
                alt="mountains"
              />
            </Parallax>
          </div>
          <div className={classes.content}>
            <div>
              <Grid
                container
                alignItems="center"
                direction="column"
                justify="center"
                className={classes.header}
              >
                <Grid item xs={12}>
                  <Typography
                    className={classnames(
                      classes.displayFont,
                      classes.textDark,
                      classes.marriedStatus
                    )}
                    variant="display4"
                    gutterBottom={true}
                  >
                    {this.state.married}
                  </Typography>
                  <Typography className={classes.scrollText}>
                    (scroll)
                  </Typography>
                  <Grow in={this.state.grow}>
                    <ArrowDownward className={classes.scrollArrow} />
                  </Grow>
                </Grid>
              </Grid>
            </div>
            <div className={classes.contentBodyContainer}>
              <div className={classes.treesContainer}>
                {/* <IdealImage
                  className={classes.trees}
                  placeholder={treesImg}
                  srcSet={[{ src: treesImg, width: 1600 }]}
                  alt="trees"
                  width={1600}
                  height={707}
                /> */}
                <img src={treesImg} className={classes.trees} alt="trees" />
              </div>
              <div className={classes.contentBody}>
                <div className={classes.timerContainer}>
                  <Grid container xl={12} justify="center">
                    <Paper className={classes.bckgDark}>
                      <Grid container className={classes.timer} xl={12}>
                        <Typography
                          variant="subheading"
                          className={classes.textGold}
                        >
                          {this.isMarried()
                            ? "We're married, huzzah!"
                            : `But they will be ${moment(
                                weddingDateTime
                              ).fromNow()}!`}
                        </Typography>
                      </Grid>
                    </Paper>
                  </Grid>
                </div>

                <Grid
                  container
                  xl={12}
                  justify="center"
                  alignItems="center"
                  direction="column"
                >
                  <Grid>
                    <Grid
                      container
                      item
                      justify="space-around"
                      className={classes.buttonGroup}
                    >
                      <Grid item className={classes.button}>
                        <Button
                          variant="outlined"
                          onClick={this.handleQuesions}
                        >
                          Wait, I have questions...
                        </Button>
                      </Grid>
                      <Grid item className={classes.button}>
                        <Button variant="outlined" onClick={this.handleTime}>
                          It's about time.
                        </Button>
                      </Grid>
                      <Grid item className={classes.button}>
                        <Button variant="outlined" onClick={this.handleUpdates}>
                          Fine, but I want updates
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    justify="center"
                    className={classes.section}
                  >
                    <Grid
                      item
                      container
                      xl={6}
                      lg={6}
                      md={8}
                      sm={12}
                      xs={12}
                      direction="column"
                    >
                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        What is this?
                      </Typography>
                      <Typography className={classes.answer}>
                        This is a website. Welcome to the internet, and the
                        golden age of information.
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        Alright, smartass. What's the deal here? Why am I just
                        finding out about this?
                      </Typography>
                      <Typography className={classes.answer}>
                        We've been dating for 8 years and engaged for over a
                        year. We decided that we would get married this year and
                        agonized for months over the logistics of making it
                        happen until we ultimately made a [somewhat impulsive]
                        decision at the end of August to keep it simple.
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        Okay. So, who's invited?
                      </Typography>
                      <Typography className={classes.answer}>
                        No one. We love all of you who would like to join us for
                        such an occassion, but we've decided that this is an
                        intimate agreement that we want to make on our own.
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        But what about the party? We wanna celebrate!
                      </Typography>
                      <Typography className={classes.answer}>
                        We got you. Although we ultimately decided we couldn't
                        work out something this year, we absolutely want an
                        opportunity to celebrate with our friends and family.
                        We're tentatively planning to have a deferred wedding
                        reception next year, Summer 2019. More plans on this to
                        follow; we'd love to see you all in OKC next year.
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        I suppose that's okay, for now. I haven't seen you guys
                        in years; what have you been up to?
                      </Typography>
                      <Typography className={classes.answer}>
                        Mostly working (as is typical of the tax-paying adult
                        experience). Spencer is a web developer at{" "}
                        <a
                          className={classes.link}
                          href="https://net22.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Net22
                        </a>
                        , occassional designer, basic handyman, and domestic
                        house husband. Lani is the Vice President of
                        Preconstruction at{" "}
                        <a
                          className={classes.link}
                          href="http://timberlakeconstruction.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Timberlake Construction
                        </a>{" "}
                        and generally wears the pants in the fam. We have a
                        furry dog child named Clu.
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        Yo, let me see that doggo.
                      </Typography>
                      <Grid
                        container
                        direction="column"
                        //alignItems="center"
                        className={classes.answer}
                      >
                        <Typography className={classes.answerNoPad}>
                          I got you. (Knew you'd ask.)
                        </Typography>
                        <Avatar
                          alt="Clu the doggo"
                          src={doggoImg}
                          className={classes.bigAvatar}
                        />
                      </Grid>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        Adorbs. So, tell me a little about the wedding plans?
                      </Typography>
                      <Typography className={classes.answer}>
                        We're getting married in Jackson Hole, WY on the
                        afternoon of September 23rd (Lani was born in Wyoming).
                        We've hired an officiant (so we can make it legal) and a
                        photographer (so we can say things like, "oh, look how
                        young and attractive we used to be" 30 years from now).
                        We're going to have a short ceremony by String Lake in
                        Grand Teton National Park. We'll hang out in the area
                        for a few days to go hiking and spend some quality time
                        taking in nature (pictured above).
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        Sounds nice. So are you guys doing a registry or
                        anything? Should I send a gift?
                      </Typography>
                      <Typography className={classes.answer}>
                        We haven't had time to set up a registry or organize
                        anything to facilitate any gifts (sharpening our quills
                        to brandish on "Thank You" cards, hand exercises in
                        order to use an instrument other than a keyboard, etc.).
                        We'd be happy to receive gifts, but for now we'd like to
                        suggest that they're deferred until the reception. We'll
                        set up a registry by then for anyone that would like to
                        bring a gift.
                      </Typography>

                      <Typography
                        gutterBottom={true}
                        className={classes.question}
                      >
                        K. Well, still...this is all very sudden...
                      </Typography>
                      <Typography className={classes.answer}>
                        Couldn't agree more. We're still scrambling to put the
                        remaining plans together, pack our stuff, and make it
                        happen. We want to express our infinite gratitude to
                        everyone who has already offered their support of our
                        decision to buck tradition a bit. We're very excited to
                        make it official after years of it already feeling that
                        way. We love all of you, and we look forward to getting
                        in touch with you soon about the deferred reception next
                        summer!
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </div>
          </div>
        </div>

        <Dialog
          open={this.state.dialogOpen}
          TransitionComponent={Transition}
          keepMounted
          onClose={this.handleClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogContent className={classnames(classes.bckgDark)}>
            <DialogContentText id="alert-dialog-slide-description">
              <Typography variant="headline" className={classes.textGold}>
                {this.state.dialogMsg}
              </Typography>
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </MuiThemeProvider>
    );
  }
}

export default withStyles(styles)(MarriageAnnouncement);



// WEBPACK FOOTER //
// ./src/views/MarriageAnnouncement.js